/* Bowkart Smart Cart Drawer v2 — "Your Shopping Bag" (Pure Enso-style) */
(() => {
  class BowkartSmartCart {
    constructor(root) {
      this.root = root;
      const $ = (s) => root.querySelector(s);
      this.panel = $('.bk-smart-cart__panel');
      this.itemsEl = $('[data-bk-cart-items]');
      this.emptyEl = $('[data-bk-empty]');
      this.loadingEl = $('[data-bk-loading]');
      this.footerEl = $('[data-bk-footer]');
      this.countTextEl = $('[data-bk-cart-count-text]');
      this.checkoutTotalEl = $('[data-bk-checkout-total]');
      this.checkoutEl = $('[data-bk-checkout]');
      this.savingsRow = $('[data-bk-savings-row]');
      this.savingsEl = $('[data-bk-savings]');
      this.goalEl = $('[data-bk-shipping-goal]');
      this.progressEl = $('[data-bk-shipping-progress]');
      this.shippingMessageEl = $('[data-bk-shipping-message]');
      this.trustEl = $('[data-bk-trust]');
      this.crossSellEl = $('[data-bk-crosssell]');
      this.recsEl = $('[data-bk-recommendations]');
      this.itemTemplate = $('[data-bk-item-template]');
      this.recTemplate = $('[data-bk-rec-template]');
      this.couponArea = $('[data-bk-coupon-area]');
      this.couponForm = $('[data-bk-coupon-form]');
      this.couponInput = $('[data-bk-coupon-input]');
      this.couponApplyBtn = $('[data-bk-coupon-apply]');
      this.couponRemoveBtn = $('[data-bk-coupon-remove]');
      this.couponMsg = $('[data-bk-coupon-msg]');
      this.nudgeEl = $('[data-bk-coupon-nudge]');
      this.nudgeText = $('[data-bk-coupon-nudge-text]');
      this.sheetEl = $('[data-bk-coupon-sheet]');
      this.sheetList = $('[data-bk-coupon-list]');
      this.summaryEl = $('[data-bk-summary]');
      this.secureEl = $('[data-bk-secure]');
      this.codEl = $('[data-bk-cod]');
      this.currency = root.dataset.currency || 'INR';
      this.locale = root.dataset.locale || 'en-IN';
      this.threshold = Number(root.dataset.freeShippingThreshold || 0);
      this.shippingFee = Number(root.dataset.shippingFee || 0);
      this.recLimit = Number(root.dataset.recommendationLimit || 3);
      this.recIntent = root.dataset.recommendationIntent || 'related';
      this.recSource = root.dataset.recommendationSource || 'ai';
      this.aiEndpoint = (root.dataset.aiEndpoint || '').trim();
      this.openAfterAdd = root.dataset.openAfterAdd === 'true';
      this.interceptCartLinks = root.dataset.interceptCartLinks === 'true';
      this.tiers = String(root.dataset.couponTiers || '')
        .split(',').map(s => s.trim().split('|'))
        .filter(p => p.length === 3 && p[0])
        .map(p => ({ code: p[0].trim().toUpperCase(), pct: Number(p[1]), min: Math.round(Number(p[2]) * 100) }))
        .filter(t => t.min > 0 && t.pct > 0)
        .sort((a, b) => a.min - b.min);
      this.config = this.readConfig();
      this.compareCache = {};      // variant_id -> compare_at_price (cents)
      this.productFetched = {};    // handle -> true
      this.fallbackCode = sessionStorage.getItem('bkCouponFallback') || '';
      this.msgOverride = null;
      this.cart = null;
      this.lastFocused = null;
      this.busy = false;
      this.refreshTimer = null;
      this.lastNetworkCartAction = 0;

      this.bind();
      this.bindCoupons();
      this.patchKallesFriendlyHooks();
      this.installCartNetworkObserver();
    }

    readConfig() {
      try { return JSON.parse(this.root.querySelector('[data-bk-config]')?.textContent || '{}'); }
      catch (_) { return {}; }
    }

    route(path) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return root.replace(/\/$/, '') + path;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          maximumFractionDigits: 0
        }).format((Number(cents) || 0) / 100);
      } catch (_) {
        return '₹' + Math.round((Number(cents) || 0) / 100).toLocaleString('en-IN');
      }
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        if (e.target.closest('[data-bk-cart-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        const minus = e.target.closest('[data-bk-qty-minus]');
        const plus = e.target.closest('[data-bk-qty-plus]');
        const remove = e.target.closest('[data-bk-remove]');
        const recAdd = e.target.closest('[data-bk-rec-add]');

        if (minus || plus || remove) {
          const item = e.target.closest('.bk-smart-cart__item');
          if (!item) return;
          const key = item.dataset.key;
          const currentQty = Number(item.dataset.quantity || 0);

          if (remove) this.changeLine(key, 0);
          if (minus) this.changeLine(key, Math.max(0, currentQty - 1));
          if (plus) this.changeLine(key, currentQty + 1);
        }

        if (recAdd) this.addRecommendation(recAdd);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.root.classList.contains('is-open')) this.close();
      });

      document.addEventListener('click', (e) => {
        if (!this.interceptCartLinks) return;
        if (e.target.closest('[data-bk-ignore-cart]')) return;

        const explicit = e.target.closest('[data-bk-cart-open]');
        const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"], [data-cart-drawer-open], .js_cart, .t4s-site-nav__cart');
        if (explicit || cartLink) {
          e.preventDefault();
          this.open();
        }
      }, true);

      // Fallback for standard HTML add-to-cart forms.
      // Kalles often adds through Ajax, which is monitored separately below.
      if (this.openAfterAdd) {
        document.addEventListener('submit', (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          const action = form.getAttribute('action') || '';
          if (action.includes('/cart/add') && (Date.now() - this.lastNetworkCartAction) > 800) {
            this.scheduleRefresh(true, 900);
          }
        }, true);
      }

      document.addEventListener('shopify:section:load', () => {
        this.refresh(false);
      });
    }

    patchKallesFriendlyHooks() {
      const self = this;

      window.BowkartCart = {
        open: () => self.open(),
        close: () => self.close(),
        refresh: () => self.refresh(false)
      };

      ['cart:updated', 'cart:refresh', 'cart:update', 't4s:cart:updated'].forEach(name => {
        document.addEventListener(name, () => this.refresh(false));
      });
    }


    scheduleRefresh(openDrawer = false, delay = 80) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(async () => {
        if (openDrawer) {
          this.lastFocused = document.activeElement;
          this.root.classList.add('is-open');
          this.panel.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('bk-cart-locked');
        }
        await this.refresh(true);
      }, delay);
    }

    installCartNetworkObserver() {
      if (window.__bkSmartCartNetworkObserverInstalled) return;
      window.__bkSmartCartNetworkObserverInstalled = true;

      const instances = () =>
        Array.from(document.querySelectorAll('[data-bk-smart-cart]'))
          .map(el => el.__bowkartSmartCart)
          .filter(Boolean);

      const notify = (url, ok = true) => {
        if (!ok || !url) return;
        const u = String(url);
        const isAdd = /\/cart\/add(?:\.js)?(?:\?|$)/.test(u);
        const isChange = /\/cart\/change(?:\.js)?(?:\?|$)/.test(u);
        const isUpdate = /\/cart\/update(?:\.js)?(?:\?|$)/.test(u);
        const isClear = /\/cart\/clear(?:\.js)?(?:\?|$)/.test(u);

        if (!(isAdd || isChange || isUpdate || isClear)) return;

        instances().forEach(instance => {
          instance.lastNetworkCartAction = Date.now();
          // Important for Kalles quick-add: refresh only AFTER the Ajax request completes.
          instance.scheduleRefresh(isAdd && instance.openAfterAdd, 120);
        });
      };

      // Observe fetch-based cart requests.
      if (window.fetch && !window.fetch.__bkWrapped) {
        const nativeFetch = window.fetch.bind(window);

        const wrappedFetch = async (...args) => {
          const requestUrl =
            typeof args[0] === 'string'
              ? args[0]
              : (args[0] && args[0].url) || '';

          const response = await nativeFetch(...args);
          try { notify(requestUrl, response.ok); } catch (_) {}
          return response;
        };

        wrappedFetch.__bkWrapped = true;
        window.fetch = wrappedFetch;
      }

      // Observe XMLHttpRequest-based Kalles quick-add requests.
      if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__bkWrapped) {
        const proto = window.XMLHttpRequest.prototype;
        const nativeOpen = proto.open;
        const nativeSend = proto.send;

        proto.open = function(method, url, ...rest) {
          this.__bkRequestUrl = url;
          return nativeOpen.call(this, method, url, ...rest);
        };

        proto.send = function(...args) {
          this.addEventListener('loadend', () => {
            try {
              notify(this.__bkRequestUrl, this.status >= 200 && this.status < 400);
            } catch (_) {}
          }, { once: true });

          return nativeSend.apply(this, args);
        };

        proto.__bkWrapped = true;
      }
    }

    async open(forceRefresh = false) {
      this.lastFocused = document.activeElement;
      this.root.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bk-cart-locked');
      await this.refresh(forceRefresh);
      requestAnimationFrame(() => this.root.querySelector('[data-bk-cart-close]')?.focus());
    }

    close() {
      this.root.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bk-cart-locked');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    async getCart() {
      const response = await fetch(this.route('/cart.js'), {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    }

    async refresh() {
      // Cart refreshes are intentionally allowed to be re-scheduled by Kalles Ajax completion.
      if (this.busy) {
        this.scheduleRefresh(false, 180);
        return;
      }
      this.busy = true;
      this.loadingEl.hidden = false;

      try {
        this.cart = await this.getCart();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        console.error('[Bowkart Smart Cart]', err);
      } finally {
        this.loadingEl.hidden = true;
        this.busy = false;
      }
    }


    /* ---------------- rendering ---------------- */
    base() { // order value before coupon discounts (sale prices)
      return (this.cart?.items || []).reduce((s, i) => s + Number(i.original_line_price || 0), 0);
    }

    renderCart() {
      const cart = this.cart;
      const isEmpty = !cart || cart.item_count === 0;

      this.emptyEl.hidden = !isEmpty;
      this.footerEl.hidden = isEmpty;
      this.itemsEl.hidden = isEmpty;
      if (this.trustEl) this.trustEl.hidden = isEmpty;
      if (this.goalEl) this.goalEl.hidden = isEmpty || !this.threshold;
      if (this.couponArea) this.couponArea.hidden = isEmpty;
      if (this.summaryEl) this.summaryEl.hidden = isEmpty;
      if (this.secureEl) this.secureEl.hidden = isEmpty;
      if (this.codEl) this.codEl.hidden = isEmpty;
      this.countTextEl.textContent = isEmpty ? '' : ` · ${cart.item_count}`;
      this.itemsEl.innerHTML = '';

      if (isEmpty) {
        this.crossSellEl.hidden = true;
        return;
      }

      cart.items.forEach(item => {
        const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.key = item.key;
        node.dataset.quantity = item.quantity;

        const url = item.url || '#';
        const image = item.featured_image?.url || item.image || '';
        const title = item.product_title || item.title || '';

        node.querySelector('[data-bk-item-url]').href = url;
        const t = node.querySelector('[data-bk-item-title]');
        t.href = url; t.textContent = title;

        const imageEl = node.querySelector('[data-bk-item-image]');
        if (image) imageEl.src = image + (image.includes('?') ? '&' : '?') + 'width=240';
        imageEl.alt = title;

        const chips = node.querySelector('[data-bk-item-variant]');
        const values = (item.options_with_values || [])
          .map(o => o.value)
          .filter(v => v && v !== 'Default Title');
        chips.innerHTML = '';
        values.forEach(v => { const c = document.createElement('span'); c.textContent = v; chips.appendChild(c); });
        chips.hidden = !values.length;

        const unit = Number(item.original_price || item.price || 0);
        const compare = Math.max(Number(this.compareCache[item.variant_id] || 0), unit);
        node.querySelector('[data-bk-item-price]').textContent = this.money(item.original_line_price || unit * item.quantity); // coupon savings are shown in the summary

        const compareEl = node.querySelector('[data-bk-item-compare]');
        const offEl = node.querySelector('[data-bk-item-off]');
        if (compare > unit) {
          compareEl.hidden = false;
          compareEl.textContent = this.money(compare * item.quantity);
          offEl.hidden = false;
          offEl.textContent = `${Math.round((compare - unit) / compare * 100)}% off`;
        }

        node.querySelector('[data-bk-qty-value]').textContent = item.quantity;
        this.itemsEl.appendChild(node);
      });

      this.updateShipping(this.base());
      this.renderCoupons();
      this.renderSummary();
      this.ensureCompareData();
    }

    async ensureCompareData() {
      const handles = [...new Set((this.cart?.items || []).map(i => i.handle).filter(h => h && !this.productFetched[h]))];
      if (!handles.length) return;
      await Promise.all(handles.map(async h => {
        this.productFetched[h] = true;
        try {
          const r = await fetch(this.route(`/products/${encodeURIComponent(h)}.js`), { headers: { Accept: 'application/json' } });
          if (!r.ok) return;
          const p = await r.json();
          (p.variants || []).forEach(v => { this.compareCache[v.id] = Number(v.compare_at_price || 0); });
        } catch (_) {}
      }));
      this.renderCart();
    }

    updateShipping(total) {
      if (!this.shippingMessageEl || !this.threshold) return;
      const remaining = Math.max(0, this.threshold - total);
      const progress = Math.min(100, Math.max(0, total / this.threshold * 100));
      this.shippingMessageEl.textContent = remaining > 0
        ? (this.config.shippingRemaining || 'Add {amount} more for FREE shipping').replace('{amount}', this.money(remaining))
        : (this.config.shippingUnlocked || "You've unlocked FREE shipping");
      if (this.progressEl) this.progressEl.style.width = progress + '%';
      this.goalEl?.classList.toggle('is-done', remaining <= 0);
    }

    appliedCodes() {
      const list = Array.isArray(this.cart?.discount_codes) ? this.cart.discount_codes : null;
      if (list) return list.map(d => ({ code: String(d.code || '').toUpperCase(), ok: d.applicable !== false }));
      return this.fallbackCode ? [{ code: this.fallbackCode, ok: true, fallback: true }] : [];
    }

    couponSaving() {
      const c = this.cart; if (!c) return 0;
      return Math.max(0, Number(c.original_total_price || 0) - Number(c.total_price || 0));
    }

    renderCoupons() {
      if (!this.couponArea) return;
      const codes = this.appliedCodes();
      const current = codes[0];
      const base = this.base();

      if (current) {
        this.couponInput.value = current.code;
        this.couponInput.readOnly = true;
        this.couponApplyBtn.hidden = true;
        this.couponRemoveBtn.hidden = false;
        this.couponForm.classList.add('is-applied');
      } else {
        if (this.couponInput.readOnly) this.couponInput.value = '';
        this.couponInput.readOnly = false;
        this.couponApplyBtn.hidden = false;
        this.couponRemoveBtn.hidden = true;
        this.couponForm.classList.remove('is-applied');
      }

      let msg = '', tone = '';
      if (this.msgOverride) { msg = this.msgOverride.text; tone = this.msgOverride.tone; }
      else if (current && current.fallback) { msg = `${current.code} will be applied at checkout.`; tone = 'ok'; }
      else if (current && current.ok) {
        const saved = this.couponSaving();
        msg = saved > 0 ? `Discount applied with ${current.code}! You saved ${this.money(saved)}` : `${current.code} applied.`;
        tone = 'ok';
      } else if (current && !current.ok) {
        const t = this.tiers.find(x => x.code === current.code);
        msg = t && base < t.min
          ? `${current.code} works on orders above ${this.money(t.min)}. Add ${this.money(t.min - base)} more.`
          : `${current.code} can't be used on this cart.`;
        tone = 'err';
      }
      this.couponMsg.hidden = !msg;
      this.couponMsg.textContent = msg;
      this.couponMsg.className = 'bk-sc-coupon__msg' + (tone ? ' is-' + tone : '');

      // ladder nudge
      const activeCode = current && current.ok ? current.code : '';
      const best = this.tiers.filter(t => base >= t.min).pop();
      const next = this.tiers.find(t => base < t.min);
      let nudge = '', action = '';
      if (best && best.code !== activeCode) {
        nudge = `Your cart qualifies for ${best.pct}% off with '${best.code}'. Tap to apply.`;
        action = best.code;
      } else if (next) {
        nudge = `Add ${this.money(next.min - base)} more to save ${next.pct}% OFF with '${next.code}'`;
      }
      this.nudgeEl.hidden = !nudge;
      this.nudgeText.textContent = nudge;
      if (action) { this.nudgeEl.dataset.apply = action; this.nudgeEl.setAttribute('role', 'button'); this.nudgeEl.tabIndex = 0; }
      else { delete this.nudgeEl.dataset.apply; this.nudgeEl.removeAttribute('role'); this.nudgeEl.removeAttribute('tabindex'); }

      // sheet list
      if (this.sheetList) {
        this.sheetList.innerHTML = this.tiers.map(t => {
          const on = activeCode === t.code;
          const ok = base >= t.min;
          const right = on ? '<span class="bk-sc-tier__ok">Applied</span>'
            : ok ? `<button type="button" class="bk-sc-tier__btn" data-bk-apply-code="${t.code}">Apply</button>`
            : `<span class="bk-sc-tier__lock">Add ${this.money(t.min - base)} more</span>`;
          return `<div class="bk-sc-tier${on ? ' is-on' : ''}${ok ? '' : ' is-locked'}"><span class="bk-sc-tier__pct">${t.pct}% OFF</span>` +
            `<div class="bk-sc-tier__body"><strong>${t.code}</strong><small>${t.pct}% off on orders above ${this.money(t.min)}</small></div>${right}</div>`;
        }).join('') || '<p class="bk-sc-sub">No coupons right now.</p>';
      }

      // checkout link carries the code when the cart API can't hold it
      if (this.checkoutEl) {
        this.checkoutEl.href = current && current.fallback ? `/checkout?discount=${encodeURIComponent(current.code)}` : '/checkout';
      }
    }

    renderSummary() {
      const c = this.cart; if (!c || !this.summaryEl) return;
      const $ = (s) => this.root.querySelector(s);
      let mrp = 0, original = 0;
      c.items.forEach(i => {
        const unit = Number(i.original_price || i.price || 0);
        const cmp = Math.max(Number(this.compareCache[i.variant_id] || 0), unit);
        mrp += cmp * i.quantity;
        original += Number(i.original_line_price || unit * i.quantity);
      });
      const itemDisc = Math.max(0, mrp - original);
      const couponDisc = this.couponSaving();
      const free = this.threshold && this.base() >= this.threshold;
      let shipTxt, shipAdd = 0;
      if (free) shipTxt = `<span class="is-save">${this.config.shipFree || 'Free'}</span>${this.shippingFee ? ` <del>${this.money(this.shippingFee)}</del>` : ''}`;
      else if (this.shippingFee) { shipTxt = this.money(this.shippingFee); shipAdd = this.shippingFee; }
      else shipTxt = this.config.shipLater || 'At checkout';
      const total = Number(c.total_price || 0) + shipAdd;

      $('[data-bk-sum-mrp]').textContent = this.money(mrp);
      $('[data-bk-sum-item]').textContent = '- ' + this.money(itemDisc);
      $('[data-bk-sum-item-row]').hidden = itemDisc <= 0;
      $('[data-bk-sum-coupon]').textContent = '- ' + this.money(couponDisc);
      $('[data-bk-sum-coupon-row]').hidden = couponDisc <= 0;
      $('[data-bk-sum-ship]').innerHTML = shipTxt;
      $('[data-bk-sum-total]').textContent = this.money(total);
      this.checkoutTotalEl.textContent = this.money(total);

      const saving = itemDisc + couponDisc;
      this.savingsRow.hidden = saving <= 0;
      this.savingsEl.textContent = this.money(saving);
    }

    /* ---------------- coupons ---------------- */
    bindCoupons() {
      if (!this.couponForm) return;
      this.couponForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const code = (this.couponInput.value || '').trim().toUpperCase();
        if (!code) { this.flash('Enter a coupon code.', 'err'); this.couponInput.focus(); return; }
        this.applyCode(code);
      });
      this.couponRemoveBtn.addEventListener('click', () => this.applyCode(''));
      this.root.addEventListener('click', (e) => {
        if (e.target.closest('[data-bk-coupon-all]')) { this.sheetEl.hidden = false; return; }
        if (e.target.closest('[data-bk-coupon-sheet-close]') || e.target === this.sheetEl) { this.sheetEl.hidden = true; return; }
        const ap = e.target.closest('[data-bk-apply-code]');
        if (ap) { this.sheetEl.hidden = true; this.applyCode(ap.dataset.bkApplyCode); return; }
        const n = e.target.closest('[data-bk-coupon-nudge][data-apply]');
        if (n) this.applyCode(n.dataset.apply);
      });
      this.nudgeEl?.addEventListener('keydown', (e) => { if ((e.key === 'Enter' || e.key === ' ') && this.nudgeEl.dataset.apply) { e.preventDefault(); this.applyCode(this.nudgeEl.dataset.apply); } });
    }

    flash(text, tone) {
      this.msgOverride = { text, tone };
      this.renderCoupons();
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.msgOverride = null; this.renderCoupons(); }, 3500);
    }

    async applyCode(code) {
      if (this.busy) return;
      this.busy = true;
      this.couponArea.classList.add('is-loading');
      try {
        const r = await fetch(this.route('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code })
        });
        const data = r.ok ? await r.json() : null;
        if (data && Array.isArray(data.discount_codes)) {
          this.cart = data;
          this.fallbackCode = ''; sessionStorage.removeItem('bkCouponFallback');
          this.msgOverride = null;
          if (!code) this.couponInput.value = '';
        } else {
          // Cart API without discount support: keep the code for checkout
          this.fallbackCode = code; code ? sessionStorage.setItem('bkCouponFallback', code) : sessionStorage.removeItem('bkCouponFallback');
          this.cart = await this.getCart();
        }
        this.renderCart();
        this.dispatchCartState();
      } catch (err) {
        console.error('[Bowkart Smart Cart] coupon', err);
        this.flash('Could not apply the code. Please try again.', 'err');
      } finally {
        this.couponArea.classList.remove('is-loading');
        this.busy = false;
      }
    }

    async changeLine(key, quantity) {
      if (this.busy) return;
      this.busy = true;

      try {
        const response = await fetch(this.route('/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Cart update failed');

        this.cart = await response.json();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        console.error('[Bowkart Smart Cart]', err);
      } finally {
        this.busy = false;
      }
    }

    async loadRecommendations() {
      this.crossSellEl.hidden = true;
      this.recsEl.innerHTML = '';

      if (!this.cart?.items?.length) return;

      let products = [];

      // 1) Bowkart AI recommendation service.
      if (this.recSource === 'ai' && this.aiEndpoint) {
        try {
          products = await this.loadAIRecommendations();
        } catch (err) {
          console.warn('[Bowkart Smart Cart] AI recommendations unavailable; using Shopify fallback.', err);
        }
      }

      // 2) Fallback to Shopify-generated recommendations.
      if (!products.length) {
        const fallbackIntent =
          this.recSource === 'shopify_complementary'
            ? 'complementary'
            : 'related';

        products = await this.loadShopifyRecommendations(fallbackIntent);
      }

      const inCartIds = new Set(this.cart.items.map(i => Number(i.product_id)));
      products = products
        .filter(p => p && !inCartIds.has(Number(p.id)))
        .slice(0, this.recLimit);

      if (!products.length) return;

      products.forEach(product => this.renderRecommendation(product));
      this.crossSellEl.hidden = this.recsEl.children.length === 0;
    }

    async loadShopifyRecommendations(intent = 'related') {
      const anchor = this.cart.items[0];
      const productId = anchor?.product_id;
      if (!productId) return [];

      const endpoint =
        `${this.route('/recommendations/products.json')}` +
        `?product_id=${encodeURIComponent(productId)}` +
        `&limit=${Math.max(this.recLimit + 2, 5)}` +
        `&intent=${encodeURIComponent(intent)}`;

      const response = await fetch(endpoint, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) return [];

      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    }

    async loadAIRecommendations() {
      const payload = {
        cart: {
          item_count: this.cart.item_count,
          total_price: this.cart.total_price,
          currency: this.currency,
          items: this.cart.items.map(item => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            handle: item.handle,
            title: item.product_title || item.title,
            variant_title: item.variant_title || '',
            product_type: item.product_type || '',
            vendor: item.vendor || '',
            quantity: item.quantity,
            price: item.final_price,
            line_price: item.final_line_price,
            properties: item.properties || {},
            selling_plan_allocation: item.selling_plan_allocation || null
          }))
        },
        limit: this.recLimit
      };

      const response = await fetch(this.aiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`AI endpoint returned ${response.status}`);

      const data = await response.json();
      const suggestions = Array.isArray(data.products) ? data.products : [];

      // AI service may return full product objects OR only Shopify handles.
      const hydrated = [];
      for (const suggestion of suggestions.slice(0, this.recLimit + 2)) {
        if (suggestion?.variants && suggestion?.title) {
          hydrated.push(suggestion);
          continue;
        }

        const handle = suggestion?.handle;
        if (!handle) continue;

        try {
          const productResponse = await fetch(
            `${this.route('/products/')}${encodeURIComponent(handle)}.js`,
            { headers: { 'Accept': 'application/json' } }
          );

          if (!productResponse.ok) continue;

          const product = await productResponse.json();
          product.ai_reason = suggestion.reason || '';
          hydrated.push(product);
        } catch (_) {}
      }

      return hydrated;
    }

    renderRecommendation(product) {
      const node = this.recTemplate.content.firstElementChild.cloneNode(true);

      const img =
        product.featured_image?.url ||
        product.featured_image ||
        product.images?.[0] ||
        '';

      const handleUrl =
        product.handle
          ? `${this.route('/products/')}${product.handle}`
          : '#';

      const url = product.url || handleUrl;

      const imageEl = node.querySelector('[data-bk-rec-image]');
      if (img) imageEl.src = typeof img === 'string' ? img : (img.src || '');
      imageEl.alt = product.title || '';

      node.querySelector('[data-bk-rec-url]').href = url;
      node.querySelector('[data-bk-rec-title]').href = url;
      node.querySelector('[data-bk-rec-title]').textContent = product.title || '';

      const aiBox = node.querySelector('[data-bk-rec-ai]');
      const aiReason = node.querySelector('[data-bk-rec-reason]');
      if (product.ai_reason && aiBox && aiReason) {
        aiBox.hidden = false;
        aiReason.textContent = product.ai_reason;
      }

      const variants = (product.variants || []).filter(v => v.available !== false);
      const select = node.querySelector('[data-bk-rec-variant]');
      const addButton = node.querySelector('[data-bk-rec-add]');

      select.innerHTML = '';

      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent =
          variant.title === 'Default Title'
            ? 'One Size'
            : variant.title;

        option.dataset.price = variant.price;
        option.dataset.compareAtPrice = variant.compare_at_price || '';
        select.appendChild(option);
      });

      const first = variants[0];

      if (!first) {
        addButton.disabled = true;
        addButton.textContent = 'Sold out';
      } else {
        this.paintRecPrice(node, first);
      }

      if (variants.length <= 1) select.hidden = true;

      select.addEventListener('change', () => {
        const selected = variants.find(
          v => String(v.id) === String(select.value)
        );
        if (selected) this.paintRecPrice(node, selected);
      });

      this.recsEl.appendChild(node);
    }

    paintRecPrice(node, variant) {
      const priceEl = node.querySelector('[data-bk-rec-price]');
      const compareEl = node.querySelector('[data-bk-rec-compare]');

      const price = Number(variant.price || 0);
      const compare = Number(variant.compare_at_price || 0);

      priceEl.textContent = this.money(price);
      compareEl.hidden = !(compare > price);
      if (compare > price) compareEl.textContent = this.money(compare);
    }

    async addRecommendation(button) {
      if (button.disabled) return;
      const card = button.closest('.bk-smart-cart__rec');
      const select = card?.querySelector('[data-bk-rec-variant]');
      const id = select?.value;
      if (!id) return;

      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const response = await fetch(this.route('/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        });

        if (!response.ok) throw new Error('Add failed');

        button.textContent = this.config.addedText || 'Added';
        setTimeout(() => {
          button.innerHTML = original;
          button.disabled = false;
        }, 900);

        this.cart = await this.getCart();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        button.textContent = this.config.errorText || 'Try again';
        setTimeout(() => {
          button.innerHTML = original;
          button.disabled = false;
        }, 1300);
      }
    }

    dispatchCartState() {
      document.dispatchEvent(new CustomEvent('bowkart:cart:updated', { detail: { cart: this.cart } }));
      const count = this.cart?.item_count || 0;
      document.querySelectorAll('[data-cart-count], .cart-count, .t4s-cart-count, hdt-cart-count').forEach(el => { el.textContent = count; });
    }
  }

  function init() {
    document.querySelectorAll('[data-bk-smart-cart]').forEach(root => {
      if (!root.__bowkartSmartCart) root.__bowkartSmartCart = new BowkartSmartCart(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
